import React from 'react'
import AppContext from 'AppContext'
import { StatusBadge } from 'components/Badges/index'
import { ActionButton } from 'components/Buttons/index'
import { adminEmail, featureEnabled } from 'components/Helpers'
import { Link } from 'react-router-dom'
import { NoAccessPage } from '../../../../../NoAccess'
import MemberRouteAndNavigation from './MemberNavigation'

import BreadCrumbsComponent from '../components/BreadCrumbs'
import SupportPinModal from './SupportPinModal'
import AnonymizeModal from './AnonymizeModal'
import { memberToken } from '../../../../../../services/api/members'
import { impersonate } from '../../../../../../services/api/mypageNext'
import selectConfig from '../../../../../../config'

const baseURL = (props) => `/operators/${props.match.params.operatorID}/memberships`

const MemberName = ({ member }) => {
  const maxLength = 31
  const statusLength = member.status.length / 2

  const name = (member.customer.first_name + ' ' + member.customer.last_name)

  if (name.length + statusLength > maxLength) {
    return name.slice(0, maxLength - statusLength) + '...'
  } else {
    return name
  }
}

const BreadCrumbs = (props) => {
  return (
    <BreadCrumbsComponent baseURL={baseURL(props)}>
      <MemberNameBreadcrumb {...props} />
    </BreadCrumbsComponent>
  )
}

const editPersonaliaRoute = ({ match, location }) => {
  const { operatorID, memberID } = match.params
  const { pathname } = location
  return pathname === `/operators/${operatorID}/members/${memberID}/edit`
}

const manageMemberRoute = (props) => {
  const { operatorID, memberID } = props.match.params
  const { pathname } = props.location
  return pathname.startsWith(
    `/operators/${operatorID}/members/${memberID}/manage`
  )
}

const SubPage = (props) => {
  return editPersonaliaRoute(props) || manageMemberRoute(props)
}

const MemberNameBreadcrumb = (props) => {
  if (!SubPage(props)) return null

  const { operatorID, memberID } = props.match.params
  const path = `/operators/${operatorID}/members/${memberID}`
  return (
    <Link to={path}>
      &nbsp;&gt;&nbsp;
      <MemberName member={props.member} />
    </Link>
  )
}

const MemberLoginButton = ({ member, user }) => {
  if (!member) return null
  // A market on the new my page uses the button below instead.
  if (featureEnabled('mypageNext', member.operator_id)) return null

  const success = (result) => {
    // redirect to mypage
    const redirectTo = selectConfig(member.operator_id).mypage.url + '?access_token=' + result.token
    window.open(redirectTo, '_blank')
  }

  const loginMember = (member) => {
    // console.log('login', member)
    memberToken(member.id, member.operator_id, success)
  }

  return (
    <div className='header-action-wrapper'>
      <ActionButton
        user={user}
        permission='memberships.visit_my_page'
        onClick={() => loginMember(member)}
        txt='View my page'
        icon='report white'
      />
    </div>
  )
}

// The same thing against the new my page (../mypage-ui-next). In a market it is
// rolled out to, it is the only my page button; elsewhere it shows beside the
// old one, for the people testing it. Hidden rather than disabled: an
// unreleased feature should be absent, not greyed out.
const MemberLoginNextButton = ({ member, user }) => {
  const { currentAdmin } = React.useContext(AppContext)

  if (!member) return null
  if (!featureEnabled('mypageNext', member.operator_id, adminEmail(currentAdmin))) return null

  const rolledOut = featureEnabled('mypageNext', member.operator_id)

  const success = (result) => {
    // The new app reads the token from the URL FRAGMENT, deliberately — unlike
    // legacy's `?access_token=`, it never reaches a server access log.
    const redirectTo = selectConfig(member.operator_id).mypage_next.url +
      '/#access_token=' + result.membership_token
    window.open(redirectTo, '_blank')
  }

  return (
    <div className='header-action-wrapper'>
      <ActionButton
        user={user}
        permission='memberships.visit_my_page'
        onClick={() => impersonate(member.id, success)}
        txt={rolledOut ? 'View my page' : 'View my page (next)'}
        icon='report white'
      />
    </div>
  )
}

const SupportPinButton = ({ member, status, setActive, user }) => {
  // Not necessary to display the SupportPin option
  // unless the membership has a status that grants access to locations
  if (!['active', 'in_trial', 'in_cancellation'].includes(status)) return null

  if (!member) return null

  return (
    <div className='header-action-wrapper'>
      <ActionButton
        user={user}
        permission='memberships.send_support_pin'
        onClick={() => setActive(true)}
        txt='Send support pin'
        icon='mailsend white'
      />
    </div>
  )
}

const SyncMemberButton = ({ member, status, sync, syncState, setSyncState, user }) => {
  // Don't display the Sync option if the membership has a cancelled/stopped status
  if (['stopped', 'cancelled', 'cancelled_in_trial', 'cancelled_in_presale'].includes(status)) return null

  if (!member) return null

  return (
    <div className='header-action-wrapper'>
      <ActionButton
        user={user}
        permission='memberships.view_memberships'
        onClick={(event) => sync(event, member, setSyncState)}
        txt='Sync'
        icon='mailsend white'
        disabled={syncState}
      />
    </div>
  )
}

const SupportPinModalWrapper = ({ active, member, onClose }) => {
  if (!active) return null

  return (
    <SupportPinModal membership={member} onClose={onClose} active={active} />
  )
}

const AnonymizeButton = ({ member, status, setActive, user }) => {
  // Don't display the Anonymise option unless the membership has a cancelled/stopped status
  if (['active', 'in_trial', 'in_cancellation', 'in_freeze'].includes(status)) return null

  if (!member) return null

  return (
    <div className='header-action-wrapper'>
      <ActionButton
        user={user}
        permission='memberships.anonymize'
        onClick={() => setActive(true)}
        txt='Anonymize'
        icon='error #FFA500'
      />
    </div>
  )
}

const AnonymizeModalWrapper = ({ active, member, onClose }) => {
  if (!active) return null

  return (
    <AnonymizeModal membership={member} onClose={onClose} active={active} />
  )
}

const MemberMainPage = (props) => {
  const [supportPinActive, setSupportPinActive] = React.useState(false)
  const [anonymizeActive, setAnonymizeActive] = React.useState(false)
  const [syncDisabled, setSyncDisabled] = React.useState(false)

  const { member, memberFromServer, memberSync, user } = props

  if (!Object.keys(member).length > 0) {
    return <NoAccessPage />
  }

  return (
    <div>
      <h1 className='with-breadcrumbs'>
        <BreadCrumbs {...props} />
        <MemberName member={member} />
        <span style={{ paddingLeft: '10px' }}>
          <StatusBadge status={member.status} />
          {member.cancellation && member.cancellation.comment
            ? <span style={{ fontSize: '0.9rem', marginLeft: '1rem' }}>{member.cancellation.comment}</span>
            : ''}
        </span>

        <MemberLoginButton member={member} user={user} />

        <MemberLoginNextButton member={member} user={user} />

        <AnonymizeButton
          status={member.status}
          member={member}
          setActive={setAnonymizeActive}
          user={user}
        />

        <SyncMemberButton
          status={member.status}
          member={member}
          sync={memberSync}
          setSyncState={setSyncDisabled}
          syncState={syncDisabled}
          user={user}
        />

        <SupportPinButton
          status={member.status}
          member={member}
          setActive={setSupportPinActive}
          user={user}
        />

      </h1>
      <SupportPinModalWrapper
        member={member}
        onClose={() => {
          setSupportPinActive(false)
          memberFromServer()
          // if on the keys page, trigger a refresh of the keys list
          const keyElement = document.querySelector('cl-sync-user-keys')
          if (keyElement) {
            keyElement.dispatchEvent(new CustomEvent('trigger-refresh', { bubbles: true, composed: true, detail: { action: 'pin' } }))
          }
        }}
        active={supportPinActive}
      />
      <AnonymizeModalWrapper
        member={member}
        onClose={() => {
          setAnonymizeActive(false)
          memberFromServer()
        }}
        active={anonymizeActive}
      />
      <MemberRouteAndNavigation {...props} />
    </div>
  )
}

const header = (props) => {
  if (editPersonaliaRoute(props)) {
    return 'Edit Personalia'
  } else {
    return 'Manage membership'
  }
}

const SubPageTopBar = ({ path, index, props }) => {
  const subPageHeader = header(props)
  return (
    <div>
      <h1 className='with-breadcrumbs'>
        <BreadCrumbs {...props} />
        {subPageHeader}
      </h1>
      <MemberRouteAndNavigation subPage={subPageHeader} {...props} />
    </div>
  )
}

const MemberWrapper = (props) => {
  if (SubPage(props)) {
    return <SubPageTopBar path='manage' index='General' props={props} />
  }
  return <MemberMainPage {...props} />
}

export default MemberWrapper
